/**
 * Binary and exclusive choice controls — Checkbox, Radio, Switch.
 *
 * 192 raw `<input type="checkbox">` elements live in 97 files, each carrying
 * its own label markup, spacing and (usually absent) dark-mode handling. These
 * three components own that markup so a caller passes a label, not a <div>.
 *
 * Checkbox vs Switch is a semantic distinction, not a style one, and the
 * components enforce it by what they accept: a Checkbox is a value you are
 * SUBMITTING as part of a form or a selection, so it has an indeterminate
 * state and belongs in a CheckboxGroup. A Switch takes effect the moment it is
 * flipped, so it has no indeterminate state and no group — if flipping it does
 * not do something immediately, it is a Checkbox.
 */
import React, { useId } from 'react';
import { FOCUS_RING } from '../mono/primitives';

export interface ChoiceBaseProps {
  label?: React.ReactNode;
  /** Secondary line under the label. */
  description?: React.ReactNode;
  className?: string;
}

/* ── Checkbox ───────────────────────────────────────────────────────────── */

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>,
    ChoiceBaseProps {
  /** Neither checked nor unchecked — a partially selected parent. */
  indeterminate?: boolean;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, indeterminate, className = '', disabled, ...rest },
  ref
) {
  const id = useId();
  const inputId = rest.id ?? id;
  return (
    <ChoiceRow
      inputId={inputId}
      label={label}
      description={description}
      disabled={disabled}
      className={className}
    >
      <input
        ref={(el) => {
          if (el) el.indeterminate = Boolean(indeterminate);
          if (typeof ref === 'function') ref(el);
          else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }}
        type="checkbox"
        id={inputId}
        disabled={disabled}
        className={`mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer accent-[var(--slate)] disabled:cursor-not-allowed ${FOCUS_RING}`}
        {...rest}
      />
    </ChoiceRow>
  );
});

/* ── Radio ──────────────────────────────────────────────────────────────── */

export interface RadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>,
    ChoiceBaseProps {}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, description, className = '', disabled, ...rest },
  ref
) {
  const id = useId();
  const inputId = rest.id ?? id;
  return (
    <ChoiceRow
      inputId={inputId}
      label={label}
      description={description}
      disabled={disabled}
      className={className}
    >
      <input
        ref={ref}
        type="radio"
        id={inputId}
        disabled={disabled}
        className={`mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer accent-[var(--slate)] disabled:cursor-not-allowed ${FOCUS_RING}`}
        {...rest}
      />
    </ChoiceRow>
  );
});

/* ── Switch ─────────────────────────────────────────────────────────────── */

export interface SwitchProps extends ChoiceBaseProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  id?: string;
  /** Accessible name when there is no visible `label`. */
  'aria-label'?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  className = '',
  id,
  ...aria
}: SwitchProps) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <ChoiceRow
      inputId={inputId}
      label={label}
      description={description}
      disabled={disabled}
      className={className}
    >
      <button
        type="button"
        role="switch"
        id={inputId}
        aria-checked={checked}
        aria-label={aria['aria-label']}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-4 w-7 shrink-0 cursor-pointer items-center border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          checked ? 'bg-[var(--slate)]' : 'bg-[var(--surface)]'
        } ${FOCUS_RING}`}
        style={{ borderColor: checked ? 'var(--slate)' : 'var(--rule-bright)' }}
      >
        <span
          aria-hidden="true"
          className={`block h-2.5 w-2.5 transition-transform ${
            checked ? 'translate-x-[0.8125rem] bg-white' : 'translate-x-[0.1875rem] bg-[var(--text-ter)]'
          }`}
        />
      </button>
    </ChoiceRow>
  );
}

/* ── Shared row ─────────────────────────────────────────────────────────── */

function ChoiceRow({
  inputId,
  label,
  description,
  disabled,
  className,
  children,
}: {
  inputId: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  className: string;
  children: React.ReactNode;
}) {
  if (!label && !description) {
    return <span className={className}>{children}</span>;
  }
  return (
    <div className={`flex items-start gap-2.5 ${disabled ? 'opacity-40' : ''} ${className}`}>
      {children}
      <div className="min-w-0">
        <label
          htmlFor={inputId}
          className={`block text-sm leading-tight text-[var(--text-pri)] ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          {label}
        </label>
        {description && (
          <p className="mt-0.5 text-xs leading-snug text-[var(--text-dim)]">{description}</p>
        )}
      </div>
    </div>
  );
}

/* ── Group ──────────────────────────────────────────────────────────────── */

export interface ChoiceGroupProps {
  legend: React.ReactNode;
  /** Row layout for two or three short options. */
  orientation?: 'vertical' | 'horizontal';
  hint?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/** Fieldset + legend around a set of Checkboxes or Radios. Screen readers
    announce the legend with each option, which loose inputs never get. */
export function ChoiceGroup({
  legend,
  orientation = 'vertical',
  hint,
  error,
  children,
  className = '',
}: ChoiceGroupProps) {
  return (
    <fieldset className={`min-w-0 border-0 p-0 ${className}`}>
      <legend className="mb-2 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
        {legend}
      </legend>
      <div className={orientation === 'horizontal' ? 'flex flex-wrap gap-x-6 gap-y-2' : 'flex flex-col gap-2'}>
        {children}
      </div>
      {error ? (
        <p className="mt-1.5 text-xs text-[var(--coral)]">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-[var(--text-dim)]">{hint}</p>
      ) : null}
    </fieldset>
  );
}
